import SectionCard from "@buildpy/ui/section-card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@buildpy/ui/table";
import type { ChangeOrderSnapshotData } from "@buildpy/core/modules/change-order/types";
import { extendDaysLabel, paymentCompareRows } from "@buildpy/core/modules/change-order/view";
import Money from "@/components/money";

// SectionCard 안 촘촘한 표 — 공통 Table 기본(h-12·p-4)을 줄이고 왼쪽 여백을 없앤다.
const HEAD = "h-auto py-1 pl-0 pr-2 text-xs font-normal";
const CELL = "py-1.5 pl-0 pr-2";

interface Props {
  data: ChangeOrderSnapshotData;
  // 굳은 문서가 아니라 지금 값으로 지은 미리보기다.
  draft: boolean;
}

// 변경 전 → 후 금액과 지급회차 재배분 — 고객이 승인하는 문서와 같은 숫자를 보인다.
const ChangeOrderPreview = ({ data, draft }: Props) => {
  return (
    <SectionCard className="text-sm" padding="sm" data-testid="change-order-preview">
      <h2 className="mb-2 flex items-baseline gap-x-2 text-sm font-semibold">
        변경 전 → 후
        {draft && <span className="text-xs font-normal text-muted-foreground">지금 값으로 계산한 미리보기</span>}
      </h2>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
        <dt className="text-muted-foreground">변경 전 계약금액</dt>
        <dd className="text-right tabular-nums"><Money value={data.base_total} /></dd>
        <dt className="text-muted-foreground">증감</dt>
        <dd className="text-right tabular-nums" data-testid="change-order-delta">
          <Money signed value={data.delta_total} />
        </dd>
        <dt className="text-muted-foreground">변경 후 계약금액</dt>
        <dd className="text-right font-semibold tabular-nums" data-testid="change-order-after">
          <Money value={data.after_total} />
        </dd>
        {data.extend_days ? (
          <>
            <dt className="text-muted-foreground">공기 증감</dt>
            <dd className="text-right tabular-nums" data-testid="change-order-extend-days-label">
              {extendDaysLabel(data.extend_days)}
            </dd>
          </>
        ) : null}
      </dl>

      <Table className="mt-3">
        <TableHeader>
          <TableRow>
            <TableHead className={HEAD}>회차</TableHead>
            <TableHead className={`${HEAD} text-right`}>변경 전</TableHead>
            <TableHead className={`${HEAD} text-right`}>변경 후</TableHead>
            <TableHead className={`${HEAD} pr-0 text-right`}>지급</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {paymentCompareRows(data).map((p) => {
            const changed = p.after !== null && p.after !== p.before;
            return (
              <TableRow key={p.seq}>
                <TableCell className={`${CELL} whitespace-nowrap`}>{p.label}</TableCell>
                <TableCell className={`${CELL} text-right tabular-nums whitespace-nowrap`}><Money value={p.before} /></TableCell>
                <TableCell
                  className={`${CELL} text-right tabular-nums whitespace-nowrap ${changed ? "font-semibold" : ""}`}>
                  <Money value={p.after} />
                </TableCell>
                <TableCell className={`${CELL} pr-0 text-right text-xs text-muted-foreground`}>{p.paid ? "지급됨" : ""}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </SectionCard>
  );
};

export default ChangeOrderPreview;
